import { Flex, VStack } from '@chakra-ui/react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import IconDropdownDownArrow from '~/assets/icons/icon-dropdown-downArrow.svg?react'
import IconDropdownUpArrow from '~/assets/icons/icon-dropdown-upArrow.svg?react'
import { useModalState } from '~/atoms/ModalState'
import { CallMode, useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { useLastOutboundPhoneNumber } from '~/atoms/WebPhoneInteractionState'
import { MangoButton } from '~/components/shared/MangoButton'
import { ContactSearchInput } from '~/components/specialized/ContactSearchInput/ContactSearchInput'
import { light } from '~/constants/theme'
import { useAnalytics } from '~/hooks/useAnalytics'
import { useOnLogout } from '~/hooks/useOnLogout'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { CallButton } from '~/pages/WebPhone/CallButton'
import { CallButtonVariants } from '~/pages/WebPhone/componentStyles/CallButton.style'
import { useKeypadStyles } from '~/pages/WebPhone/componentStyles/Keypad.style'
import { Keypad } from '~/pages/WebPhone/KeyPad'
import { usePostClickToCall } from '~/queries/callQueries'
import { AnalyticsNamespace } from '~/types/Analytics'
import { WebPhoneAnalyticsEvents } from '~/types/Analytics/WebPhoneAnalyticsEvents'
import { Size } from '~/types/ThemeTypes'
import { formatPhoneNumber } from '~/utils/formatter'

import { cleanDialstring } from '@mangovoice/core'
import { PhoneCallStatus } from '@mangovoice/types'

import { FontStyle, Text } from './core'
import { TextButtonVariants } from './shared/componentStyles/TextButton.style'
import { DropdownTextButton } from './shared/Dropdown/DropdownTextButton'
import { MenuPlacement } from './shared/Dropdown/Menu'
import { MangoModal } from './shared/MangoModal'
import SpinningCirclesLoader from './shared/SpinningCirclesLoader'
import { OutboundPhoneSearchInput } from './specialized/OutboundPhoneSearchInput/OutboundPhoneSearchInput'

const ModalBodyWrapper = styled.div`
  width: 100%;
  padding: 0 1.25rem 1.25rem 1.25rem;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-content: center;
  justify-content: center;
`
const DropdownWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0px 0px 12px 0px;
  gap: 8px;
`
const TextWrapper = styled.div`
  display: flex;
  height: 300px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
`
const RingingWrapper = styled.div`
  display: flex;
  height: 300px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
`
const AnimationWrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  width: 300px;
`

const NewCallModal = () => {
  const {
    settings: {
      clickToCall: { clickToCallMode },
    },
    canUseWebPhone,
  } = useSettingsState()
  const { currentUserExtensionId } = useUserState()
  const {
    settings: {
      webPhone: { enableCallerIdSelection },
    },
  } = useSettingsState()
  const [lastUsedOutboundPhone, setLastUsedOutboundPhone] =
    useLastOutboundPhoneNumber()
  const { t } = useTranslation()
  const [callModeMenuOpen, setCallModeMenuOpen] = useState<boolean>(false)
  const [startCallButtonPressed, setStartCallButtonPressed] = useState(false)
  const [callModeSelection, setCallModeSelection] = useState<CallMode | null>(
    null,
  )
  const [phoneNumber, setPhoneNumber] = useState('')
  const [tempCalleeInfo, setTempCalleeInfo] = useState<{
    contactNumber: ContactNumber
    contact: ContactDetails
  }>()
  const [startedClickToCall, setStartedClickToCall] = useState(false)
  const [startedWebphoneCall, setStartedWebphoneCall] = useState(false)
  const [closeTimeout, setCloseTimeout] = useState<NodeJS.Timeout | null>(null)

  const resetStateToDefaults = () => {
    setStartCallButtonPressed(false)
    setCallModeSelection(null)
    setPhoneNumber('')
    setStartedClickToCall(false)
    setStartedWebphoneCall(false)
    setCloseTimeout(null)
  }

  const { mutate: postClickToCall, isError } = usePostClickToCall({
    extensionId: currentUserExtensionId ?? 0,
  })

  const {
    callModal: [callModalState, setCallModalState],
  } = useModalState()

  const {
    state: {
      phoneCalls: {
        active: { activeCall },
        isAtMaxCallLimit,
      },
    },
    actions: { startCall },
  } = usePhoneEndpoint({})

  useOnLogout(() => {
    setLastUsedOutboundPhone(null)
  }, 'useLastOutboundPhoneNumber')

  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)

  const CallModeMap = new Map<CallMode, { description: string; note?: string }>(
    [
      [
        CallMode.CLICK_TO_CALL,
        {
          description: t('newCallModal.sendToDeskPhone'),
        },
      ],
    ],
  )
  if (canUseWebPhone) {
    CallModeMap.set(CallMode.WEBPHONE, {
      description: t('newCallModal.sendToWebPhone'),
    })
  }

  if (callModalState.callee) {
    // The local formatter and the core formatter work differently with 11 digit numbers.
    // Passing the number through the local first gives us a standard launch point to work with.
    const formattedPhone = formatPhoneNumber(callModalState.getNumber?.())
    setPhoneNumber(formattedPhone)
    setCallModalState({ ...callModalState, callee: undefined })
    if (typeof callModalState.callee !== 'string') {
      setTempCalleeInfo(callModalState.callee)
    }
  }

  const activeCallMode =
    callModalState.overrideCallMode ?? callModeSelection ?? clickToCallMode

  const handleChangePhone = (newPhoneNumber: string) => {
    setPhoneNumber(newPhoneNumber)
    setTempCalleeInfo(undefined)
  }
  const handleChangeCallModeSelection = (callMode: CallMode) => {
    setCallModalState({ ...callModalState, overrideCallMode: undefined })
    setCallModeSelection(callMode)
  }

  const handleCloseModal = () => {
    setCallModalState({ open: false })

    setPhoneNumber('')
    setCallModeSelection(null)
    setStartCallButtonPressed(false)
    setStartedClickToCall(false)
    setStartedWebphoneCall(false)
    setCloseTimeout(null)
  }

  const handleClickToCall = () => {
    postClickToCall({ phoneNumber: cleanDialstring(phoneNumber) })
    setStartedClickToCall(true)
    track(WebPhoneAnalyticsEvents.ClickToCallDeskPhone)
  }
  const handleWebphoneCall = () => {
    if (
      tempCalleeInfo &&
      cleanDialstring(
        formatPhoneNumber(tempCalleeInfo.contactNumber.number),
      ) === cleanDialstring(phoneNumber)
    ) {
      startCall(tempCalleeInfo, lastUsedOutboundPhone?.number)
    } else {
      startCall(cleanDialstring(phoneNumber), lastUsedOutboundPhone?.number)
    }
    setTempCalleeInfo(undefined)
    setStartedWebphoneCall(true)
    track(WebPhoneAnalyticsEvents.ClickToCallWebPhone)
    track(WebPhoneAnalyticsEvents.StartCallFromModal)
  }

  const handleKeyPadClick = (digit: string) => {
    setPhoneNumber((phonenumber) => phonenumber + digit)
  }

  const handleMenuOpenChange = () => {
    setCallModeMenuOpen(!callModeMenuOpen)
    return !callModeMenuOpen
  }

  const getCallButtonStatus = (callMode: CallMode) => {
    let disabled = true

    if (phoneNumber && !startCallButtonPressed) {
      disabled = false
    }

    if (isAtMaxCallLimit && activeCallMode === CallMode.WEBPHONE) {
      disabled = true
    }

    if (callMode === CallMode.CLICK_TO_CALL) {
      if (phoneNumber.length < 7) {
        disabled = true
      }
    }

    return disabled
  }

  const isCallButtonDisabled = getCallButtonStatus(activeCallMode)
  const handleCallButtonClick = () => {
    if (isCallButtonDisabled) return
    if (!startCallButtonPressed) {
      setStartCallButtonPressed(true)
      switch (activeCallMode) {
        case CallMode.CLICK_TO_CALL:
          handleClickToCall()
          break
        case CallMode.WEBPHONE:
          handleWebphoneCall()
          break
        default:
          break
      }

      const timeout = setTimeout(() => {
        handleCloseModal()
      }, 2 * 1000)
      setCloseTimeout(timeout)
    }
  }

  const handleTryAgainButtonClicked = () => {
    resetStateToDefaults()
  }

  const dropdownOptions = Array.from(CallModeMap, ([key, content]) => ({
    value: key,
    content: content.description,
  }))

  if (!canUseWebPhone && activeCallMode === CallMode.WEBPHONE) {
    setCallModeSelection(CallMode.CLICK_TO_CALL)
  }

  if (isError && closeTimeout) {
    clearTimeout(closeTimeout)
    setCloseTimeout(null)
  }

  const shouldShowToDropdown =
    dropdownOptions.length > 1 && !startCallButtonPressed
  const shouldShowFromDropdown = !startCallButtonPressed
  const isReadyForNewCall = !startedClickToCall && !startedWebphoneCall
  const isClickToCallInProgress = startedClickToCall && !isError
  const hasClickToCallFailed = startedClickToCall && isError
  const isWebphoneCallInProgress =
    startedWebphoneCall && activeCall?.status === PhoneCallStatus.RINGING

  const styles = useKeypadStyles({
    size: Size.sm,
  })

  return (
    <MangoModal
      isOpen={callModalState.open}
      header={t('newCallModal.header')}
      onClose={handleCloseModal}
    >
      <ModalBodyWrapper>
        {enableCallerIdSelection && shouldShowFromDropdown && (
          <Flex direction={'column'}>
            <Text
              style={{
                fontSize: '.75rem',
                fontWeight: '400',
                color: light.gray900,
              }}
            >
              {t('newCallModal.from')}
            </Text>
            <DropdownWrapper>
              <OutboundPhoneSearchInput
                scope="new_call_modal"
                onValueChanged={(value) => {
                  setLastUsedOutboundPhone(value)
                }}
                value={
                  lastUsedOutboundPhone
                    ? `${lastUsedOutboundPhone.name}: ${formatPhoneNumber(lastUsedOutboundPhone.number)}`
                    : undefined
                }
              />
            </DropdownWrapper>
          </Flex>
        )}

        {isReadyForNewCall && (
          <Flex direction={'column'}>
            {enableCallerIdSelection && (
              <Text
                style={{
                  fontSize: '.75rem',
                  fontWeight: '400',
                  color: light.gray900,
                }}
              >
                {t('newCallModal.to')}
              </Text>
            )}
            <VStack width="100%" align="stretch">
              <VStack spacing="0.25rem" pb="0.5rem">
                <ContactSearchInput
                  scope="new_thread_modal"
                  value={phoneNumber}
                  onValueChanged={handleChangePhone}
                />
              </VStack>

              <Keypad
                size={Size.sm}
                onButtonClick={handleKeyPadClick}
                centerButton={
                  <CallButton
                    variant={CallButtonVariants.Start}
                    disabled={isCallButtonDisabled}
                    onClick={handleCallButtonClick}
                    sx={styles.callButton}
                  />
                }
              ></Keypad>
              {shouldShowToDropdown && (
                <Flex justifyContent="center">
                  <DropdownTextButton
                    placeholder={
                      CallModeMap.get(activeCallMode)?.description ?? ''
                    }
                    items={dropdownOptions}
                    rightIcon={
                      callModeMenuOpen
                        ? IconDropdownUpArrow
                        : IconDropdownDownArrow
                    }
                    onSelect={handleChangeCallModeSelection}
                    onMenuOpenChange={handleMenuOpenChange}
                    menuOptions={{
                      width: '20rem',
                      placement: MenuPlacement.BottomCenter,
                    }}
                    variant={TextButtonVariants.Gray_1300}
                  />
                </Flex>
              )}
            </VStack>
          </Flex>
        )}
        {isClickToCallInProgress && (
          <TextWrapper>
            <Text color="black" fontStyle={FontStyle.Normal}>
              {t('newCallModal.attemptingToEstablishCallTo', { phoneNumber })}
            </Text>
            <Text color="black" fontStyle={FontStyle.Small}>
              {t('newCallModal.answerHandsetWhenItRings')}
            </Text>
          </TextWrapper>
        )}
        {hasClickToCallFailed && (
          <TextWrapper>
            <Text color="black" fontStyle={FontStyle.Normal}>
              {t('newCallModal.failedToEstablishCallTo', { phoneNumber })}
            </Text>
            <Text color="black" fontStyle={FontStyle.Small}>
              {t('newCallModal.checkInternetConnection')}
            </Text>
            <MangoButton onClick={handleTryAgainButtonClicked}>
              {t('newCallModal.tryAgain')}
            </MangoButton>
          </TextWrapper>
        )}
        {isWebphoneCallInProgress && (
          <RingingWrapper>
            <Text color="black" fontStyle={FontStyle.Normal}>
              {t('newCallModal.establishingCallTo', { phoneNumber })}
            </Text>
            <AnimationWrapper>
              <SpinningCirclesLoader />
            </AnimationWrapper>
          </RingingWrapper>
        )}
      </ModalBodyWrapper>
    </MangoModal>
  )
}

export default NewCallModal
